import React, { useState } from 'react';
import './anchor-styles.css';

/**
 * Invoice Filters Component
 */
const InvoiceFilters = ({ onFilterChange, suppliers = [] }) => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: 'last30days',
    customDateFrom: '',
    customDateTo: '',
    amountRange: '',
    customAmountMin: '',
    customAmountMax: '',
    sortBy: 'newest'
  });

  // Update local state only, don't trigger API call
  const handleInputChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  // Build filter params and trigger API call
  const buildFilterParams = (currentFilters) => {
    const filterParams = {
      search: currentFilters.search.trim(),
      status: currentFilters.status,
      sortBy: currentFilters.sortBy
    };

    // Handle date range
    if (currentFilters.dateRange !== 'custom') {
      const dateParams = getDateRange(currentFilters.dateRange);
      filterParams.dateFrom = dateParams.from;
      filterParams.dateTo = dateParams.to;
    } else {
      filterParams.dateFrom = currentFilters.customDateFrom;
      filterParams.dateTo = currentFilters.customDateTo;
    }

    // Handle amount range
    if (currentFilters.amountRange !== 'custom' && currentFilters.amountRange) {
      const amountParams = getAmountRange(currentFilters.amountRange);
      filterParams.amountMin = amountParams.min;
      filterParams.amountMax = amountParams.max;
    } else if (currentFilters.amountRange === 'custom') {
      filterParams.amountMin = currentFilters.customAmountMin;
      filterParams.amountMax = currentFilters.customAmountMax;
    }

    return filterParams;
  };

  // Apply filters button click
  const handleApplyFilters = () => {
    const filterParams = buildFilterParams(filters);
    onFilterChange(filterParams);
  };

  // Search on Enter key press
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  const handleReset = () => {
    const resetFilters = {
      search: '',
      status: '',
      dateRange: 'last30days',
      customDateFrom: '',
      customDateTo: '',
      amountRange: '',
      customAmountMin: '',
      customAmountMax: '',
      sortBy: 'newest'
    };
    setFilters(resetFilters);
    onFilterChange({});
  };

  return (
    <div className="invoice-filters" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '4px', marginBottom: '20px', border: '1px solid #e7eaec' }}>
      {/* Single Row Layout */}
      <div className="row" style={{ alignItems: 'flex-end', marginBottom: '15px' }}>
        {/* Status */}
        <div className="col-md-2">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#676a6c' }}>Status</label>
            <select
              className="form-control"
              value={filters.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              style={{ height: '36px' }}
            >
              <option value="">All Status</option>
              <option value="New">New</option>
              <option value="Accepted">Accepted</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Date Range */}
        <div className="col-md-2">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#676a6c' }}>Date Range</label>
            <select
              className="form-control"
              value={filters.dateRange}
              onChange={(e) => handleInputChange('dateRange', e.target.value)}
              style={{ height: '36px' }}
            >
              <option value="last7days">Last 7 Days</option>
              <option value="last30days">Last 30 Days</option>
              <option value="thisMonth">This Month</option>
              <option value="lastMonth">Last Month</option>
            </select>
          </div>
        </div>

        {/* Amount Range */}
        <div className="col-md-2">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#676a6c' }}>Amount Range</label>
            <select
              className="form-control"
              value={filters.amountRange}
              onChange={(e) => handleInputChange('amountRange', e.target.value)}
              style={{ height: '36px' }}
            >
              <option value="">Any Amount</option>
              <option value="under1L">&lt; ₹1 Lakh</option>
              <option value="1to5L">₹1-5 Lakhs</option>
              <option value="5to10L">₹5-10 Lakhs</option>
              <option value="10to50L">₹10-50 Lakhs</option>
              <option value="above50L">&gt; ₹50 Lakhs</option>
            </select>
          </div>
        </div>

        {/* Sort By */}
        <div className="col-md-2">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#676a6c' }}>Sort By</label>
            <select
              className="form-control"
              value={filters.sortBy}
              onChange={(e) => handleInputChange('sortBy', e.target.value)}
              style={{ height: '36px' }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amountHigh">Highest Amount</option>
              <option value="amountLow">Lowest Amount</option>
              <option value="dueDate">Due Date (Earliest)</option>
            </select>
          </div>
        </div>

        {/* Search by Invoice/Supplier */}
        <div className="col-md-2">
          <div className="form-group" style={{ marginBottom: '0' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '5px', display: 'block', color: '#676a6c', whiteSpace: 'nowrap' }}>Invoice/Supplier Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => handleInputChange('search', e.target.value)}
              onKeyPress={handleSearchKeyPress}
              style={{ height: '36px' }}
            />
          </div>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="row" style={{ marginTop: '0' }}>
        <div className="col-md-12" style={{ textAlign: 'left' }}>
          <button 
            className="btn btn-primary" 
            onClick={handleApplyFilters}
            style={{ marginRight: '10px', minWidth: '140px', padding: '8px 16px' }}
          >
            <i className="fa fa-filter"></i> Apply Filters
          </button>
          <button 
            className="btn btn-default" 
            onClick={handleReset}
            style={{ minWidth: '140px', padding: '8px 16px' }}
          >
            <i className="fa fa-refresh"></i> Reset Filters
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getDateRange = (range) => {
  const today = new Date();
  const from = new Date();
  
  switch (range) {
    case 'today':
      return { from: formatDate(today), to: formatDate(today) };
    case 'last7days':
      from.setDate(today.getDate() - 7);
      return { from: formatDate(from), to: formatDate(today) };
    case 'last30days':
      from.setDate(today.getDate() - 30);
      return { from: formatDate(from), to: formatDate(today) };
    case 'thisMonth':
      from.setDate(1);
      return { from: formatDate(from), to: formatDate(today) };
    case 'lastMonth':
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: formatDate(lastMonth), to: formatDate(lastMonthEnd) };
    default:
      return { from: '', to: '' };
  }
};

const getAmountRange = (range) => {
  switch (range) {
    case 'under1L':
      return { min: 0, max: 100000 };
    case '1to5L':
      return { min: 100000, max: 500000 };
    case '5to10L':
      return { min: 500000, max: 1000000 };
    case '10to50L':
      return { min: 1000000, max: 5000000 };
    case 'above50L':
      return { min: 5000000, max: null };
    default:
      return { min: null, max: null };
  }
};

const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

export default InvoiceFilters;
